import Checkbox from '@mui/material/Checkbox'
import Icon from '@mui/material/Icon'
import { styled } from '@mui/material/styles'
import MUITableCell from '@mui/material/TableCell'
import Typography from '@mui/material/Typography'
import React from 'react'

import { type SchemaContact } from '@/__generated/es-api'
import CountryFlag from '@/app/component/atom/flag'
import Tooltip from '@/app/component/atom/tooltip'
import IconText from '@/app/component/layout/icon-text'
import TableCell from '@/app/component/layout/table/cell'
import TableRow from '@/app/component/layout/table/row'
import { formatName, formatPhoneNumber, parsePhoneNumber } from '@/app/service/util/format'

type Props = {
  contact: SchemaContact
  isLoading: boolean
  isSelected: boolean
  itemId: number
  selectionDisabledReason: string
  onSelect: (contactId: number, checked: boolean) => void
}

const ContactsTableRow: React.FC<Props> = ({
  contact,
  isLoading,
  isSelected,
  itemId,
  selectionDisabledReason,
  onSelect,
}) => {
  const subStats = contact?.subscription
  const { activeId = 0, totalCount = 0, doneCount = 0, errorCount = 0, unsubbedCount = 0 } = subStats || {}

  const contactName = formatName(contact.firstName, contact.lastName)
  const isSelectionDisabled = !!selectionDisabledReason

  const renderPhoneNumber = () => {
    if (!contact.fullPhoneNumber) return null

    const { country = '' } = parsePhoneNumber(contact.fullPhoneNumber)
    return (
      <IconText>
        <CountryFlag code={country} />
        <Typography component="span">{formatPhoneNumber(`+${contact.fullPhoneNumber}`)}</Typography>
      </IconText>
    )
  }

  const renderSubscriptionStatus = () => {
    return (
      <IconText>
        <Icon>send</Icon>
        {activeId > 0 || totalCount > 0 ? (
          <Typography className="sub-status">
            {activeId === 0 && totalCount > 0 && 'Completed'}
            {activeId > 0 && 'In progress'}
            <br />
            <Typography className="sub-stats" color="textSecondary" component="span">
              {doneCount} finished, {unsubbedCount} unsubscribed, {errorCount} failure
            </Typography>
          </Typography>
        ) : (
          <Typography className="sub-status">Not subscribed</Typography>
        )}
      </IconText>
    )
  }

  return (
    <TableRow
      data-testid="contacts-table-row"
      key={contact.id}
      isLoading={isLoading}
      onClick={() => {
        if (!isSelectionDisabled) {
          onSelect(itemId, !isSelected)
        }
      }}
    >
      <MUITableCell padding="checkbox">
        <Tooltip title={selectionDisabledReason}>
          <span onClick={(event) => event.stopPropagation()}>
            <Checkbox
              checked={isSelected}
              disabled={isSelectionDisabled}
              slotProps={{ input: { 'aria-label': `Select ${contactName || `contact ${itemId}`}` } }}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => onSelect(itemId, event.target.checked)}
            />
          </span>
        </Tooltip>
      </MUITableCell>
      <StyledTableCell index={0}>
        <IconText>
          <Icon>person_outline</Icon>
          <Typography component="span" title={contactName}>
            {contactName}
          </Typography>
        </IconText>
      </StyledTableCell>
      <StyledTableCell index={1}>{renderPhoneNumber()}</StyledTableCell>
      <StyledTableCell index={2}>{renderSubscriptionStatus()}</StyledTableCell>
    </TableRow>
  )
}

const StyledTableCell = styled(TableCell)(() => ({
  '& .MuiTypography-root': {
    maxWidth: '30vw',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}))

export default ContactsTableRow
